import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Table, Button, message, Input } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import "./InserirCotacao.css";
import api from '../../../services/api';
import API_CONFIG from '../../../config/api.config';

const InserirCotacao = () => {
  const { solicitacaoId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [solicitacao, setSolicitacao] = useState(location.state?.solicitacao || null);
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [valorTotal, setValorTotal] = useState(0);
  const [itensSelecionados, setItensSelecionados] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // Define a largura de quebra para mobile

  useEffect(() => {
    // Verificar autenticação
    const tipoUsuario = localStorage.getItem('tipoUsuario');
    const idUsuario = localStorage.getItem('idUsuario');

    if (!tipoUsuario || !idUsuario || tipoUsuario !== 'FORNECEDOR') {
      message.error('Acesso não autorizado. Por favor, faça login novamente.');
      navigate('/');
      return;
    }

    // Buscar detalhes da solicitação e itens
    const buscarDetalhesSolicitacao = async () => {
      try {
        setLoading(true);
        const response = await api.get(API_CONFIG.ENDPOINTS.SOLICITACOES.ITENS_FORNECEDOR(solicitacaoId));
        //console.log('Resposta da API:', response.data);

        if (response.data.status && response.data.solicitacao) {
          const dadosSolicitacao = response.data.solicitacao;
          
          // Atualizar dados da solicitação
          setSolicitacao(dadosSolicitacao);

          // Preparar itens para a tabela
          const itensFormatados = dadosSolicitacao.itens.map(item => ({
            ITE_ID: item.IT_ID,
            ITE_DESCRICAO: item.IT_DESCRICAO,
            ITE_QUANTIDADE: parseFloat(item.IT_QUANTIDADE),
            ITE_UNIDADE: "UN", // Assumindo unidade padrão, ajuste se necessário
            valorUnitario: 0,
            valorTotal: 0
          }));

          setItens(itensFormatados);
        } else {
          message.error('Erro ao carregar dados da solicitação');
        }
      } catch (error) {
        //console.error('Erro ao buscar detalhes da solicitação:', error);
        message.error('Erro ao carregar dados da solicitação');
      } finally {
        setLoading(false);
      }
    };

    buscarDetalhesSolicitacao();
  }, [solicitacaoId, navigate]);

  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  const handleVoltar = () => {
    navigate('/fornecedor/home');
  };

  const handleInserirCotacao = () => {
    setModoEdicao(true);
  };

  const handleValorUnitarioChange = (id, valor) => {
    const novosItens = itens.map(item => {
      if (item.ITE_ID === id) {
        const valorUnitario = parseFloat(valor) || 0;
        const valorTotal = valorUnitario * item.ITE_QUANTIDADE;
        return { ...item, valorUnitario, valorTotal };
      }
      return item;
    });
    setItens(novosItens);
    
    // Calcular valor total geral apenas dos itens selecionados
    const total = novosItens
      .filter(item => itensSelecionados.includes(item.ITE_ID))
      .reduce((acc, item) => acc + item.valorTotal, 0);
    setValorTotal(total);
  };

  const handleSelecionarItem = (id) => {
    setItensSelecionados(prev => {
      const novosSelecionados = prev.includes(id)
        ? prev.filter(itemId => itemId !== id)
        : [...prev, id];
      
      // Recalcular valor total apenas dos itens selecionados
      const total = itens
        .filter(item => novosSelecionados.includes(item.ITE_ID))
        .reduce((acc, item) => acc + item.valorTotal, 0);
      setValorTotal(total);
      
      return novosSelecionados;
    });
  };

  const handleEnviarCotacao = async () => {
    // Validar se há itens selecionados
    if (itensSelecionados.length === 0) {
      message.error('Selecione pelo menos um item para enviar.');
      return;
    }

    // Validar se todos os itens selecionados têm valor unitário
    const itensSemValor = itens
      .filter(item => itensSelecionados.includes(item.ITE_ID) && item.valorUnitario <= 0);
    
    if (itensSemValor.length > 0) {
      message.error('Todos os itens selecionados devem ter um valor unitário maior que zero.');
      return;
    }

    try {
      setLoading(true);
      const idUsuario = localStorage.getItem('idUsuario');
      const idFornecedor = localStorage.getItem('idFornecedor');

      // Preparar payload da cotação
      const payload = {
        COT_FOR_ID: parseInt(idFornecedor),
        COT_SOL_ID: parseInt(solicitacao.SOL_ID),
        COT_USU_ID: parseInt(idUsuario),
        itens: itens
          .filter(item => itensSelecionados.includes(item.ITE_ID))
          .map(item => ({
            IC_IT_ID: item.ITE_ID,
            IC_VALOR: parseFloat(item.valorUnitario.toFixed(2))
          }))
      };

      //console.log('Enviando cotação:', payload);
      //console.log('Endpoint:', API_CONFIG.ENDPOINTS.COTACOES.CADASTRAR);
      
      const response = await api.post(API_CONFIG.ENDPOINTS.COTACOES.CADASTRAR, payload);
      
      if (response.data.status) {
        message.success('Cotação enviada com sucesso!');
        navigate('/fornecedor/home');
      } else {
        message.error('Erro ao enviar cotação. Tente novamente.');
      }
    } catch (error) {
      //console.error('Erro ao enviar cotação:', error);
      
      // Tratar erro 422 (validação)
      if (error.response?.status === 422) {
        const errorData = error.response.data;
        //console.log('Detalhes do erro 422:', errorData);
        
        // Tentar extrair mensagem de erro específica
        let errorMessage = 'Erro de validação. Verifique os dados enviados.';
        
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.errors) {
          // Se tiver erros específicos, mostrar o primeiro
          const firstError = Object.values(errorData.errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        }
        
        message.error(errorMessage);
      } else {
        message.error('Erro ao enviar cotação. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="inserir-cotacao-loading">Carregando...</div>;
  }

  if (!solicitacao) {
    return <div className="inserir-cotacao-error">Solicitação não encontrada</div>;
  }

  const columns = [
    {
      title: '',
      dataIndex: 'selecionado',
      key: 'selecionado',
      width: 40,
      render: (_, record) => (
        <input
          type="checkbox"
          checked={itensSelecionados.includes(record.ITE_ID)}
          onChange={() => handleSelecionarItem(record.ITE_ID)}
          disabled={!modoEdicao}
        />
      )
    },
    {
      title: 'Nº',
      dataIndex: 'ITE_ID',
      key: 'ITE_ID',
      width: 60
    },
    {
      title: 'Descrição',
      dataIndex: 'ITE_DESCRICAO',
      key: 'ITE_DESCRICAO',
    },
    {
      title: 'Quantidade',
      dataIndex: 'ITE_QUANTIDADE',
      key: 'ITE_QUANTIDADE',
      width: 120,
      render: (quantidade, record) => `${quantidade} ${record.ITE_UNIDADE}`
    },
    {
      title: 'Valor Unitário',
      key: 'valorUnitario',
      width: 150,
      render: (_, record) => (
        modoEdicao ? (
          <Input
            type="number"
            value={record.valorUnitario || ''}
            onChange={(e) => handleValorUnitarioChange(record.ITE_ID, e.target.value)}
            placeholder="R$ 0,00"
            min="0"
            step="0.01"
            disabled={!itensSelecionados.includes(record.ITE_ID)}
          />
        ) : null
      )
    },
    {
      title: 'Valor Total',
      key: 'valorTotal',
      width: 150,
      render: (_, record) => (
        modoEdicao ? (
          <span>R$ {record.valorTotal.toFixed(2)}</span>
        ) : null
      )
    }
  ];

  return (
    <div className="inserir-cotacao-container">
      <div className="inserir-cotacao-wrapper">
        <h1 className="inserir-cotacao-title">Inserir Cotação</h1>
        <div className="inserir-cotacao-content">
          <div className="inserir-cotacao-header-box">
            <div
              style={{
                display: isMobile ? 'flex' : 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'flex-start' : 'center',
                flexWrap: 'wrap',
                gap: 16
              }}
            >
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#001f4d' }}>
                Nº da solicitação: <span style={{ color: '#001f4d' }}>{solicitacao.SOL_ID}</span>
                {isMobile && (
                  <div style={{ fontWeight: 900, color: '#222', fontSize: '1rem', textAlign: 'left', marginTop: 4 }}>
                    Status: <span style={{ color: 'green', fontWeight: 600, marginLeft: 8 }}>{solicitacao.SOL_STATUS}</span>
                  </div>
                )}
              </div>
              {!isMobile && (
                <div style={{ fontWeight: 900, color: '#222', fontSize: '1rem', textAlign: 'right', minWidth: 128, flex: 1, justifyContent: 'flex-end', display: 'flex' }}>
                  Status: <span style={{ color: 'green', fontWeight: 600, marginLeft: 8 }}>{solicitacao.SOL_STATUS}</span>
                </div>
              )}
            </div>
            <div style={{margin: '6px 0'}}>
              <b>Prefeitura:</b> <span style={{fontWeight: 400}}>{solicitacao.prefeitura?.PRE_NOME}</span>
            </div>
            <div style={{margin: '6px 0'}}>
              <b>Veículo:</b> <span style={{fontWeight: 400}}>{solicitacao.veiculo?.VEI_DESCRICAO} ({solicitacao.veiculo?.VEI_PLACA})</span>
            </div>
            <div style={{margin: '6px 0'}}>
              <b>Tipo:</b> <span style={{fontWeight: 400}}>{solicitacao.veiculo?.VEI_TIPO}</span>
            </div>
            <div style={{margin: '6px 0'}}>
              <b>Data de abertura:</b> <span style={{fontWeight: 400}}>{new Date(solicitacao.SOL_DATA_VINCULACAO).toLocaleDateString('pt-BR')}</span>
            </div>
          </div>

          {isMobile ? (
            <div>
              {itens.map((item, idx) => (
                <div
                  key={item.ITE_ID}
                  className="inserir-cotacao-card"
                  onClick={e => {
                    // Evita conflito se clicar na checkbox
                    if (e.target.type !== 'checkbox') handleSelecionarItem(item.ITE_ID);
                  }}
                  style={{ cursor: modoEdicao ? 'pointer' : 'default', opacity: modoEdicao ? 1 : 0.7 }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <input
                      type="checkbox"
                      checked={itensSelecionados.includes(item.ITE_ID)}
                      onChange={() => handleSelecionarItem(item.ITE_ID)}
                      disabled={!modoEdicao}
                      style={{ marginRight: 8 }}
                      onClick={e => e.stopPropagation()} // Evita propagação para o card
                    />
                    <b>Nº:</b> {item.ITE_ID}
                  </div>
                  <div><b>Descrição:</b> {item.ITE_DESCRICAO}</div>
                  <div><b>Quantidade:</b> {item.ITE_QUANTIDADE} {item.ITE_UNIDADE}</div>
                  {modoEdicao && (
                    <>
                      <div>
                        <b>Valor Unitário:</b>
                        <Input
                          type="number"
                          value={item.valorUnitario || ''}
                          onChange={e => handleValorUnitarioChange(item.ITE_ID, e.target.value)}
                          placeholder="R$ 0,00"
                          min="0"
                          step="0.01"
                          disabled={!itensSelecionados.includes(item.ITE_ID)}
                          style={{ width: 120, marginLeft: 8 }}
                          onClick={e => e.stopPropagation()} // Evita propagação para o card
                        />
                      </div>
                      <div>
                        <b>Valor Total:</b> R$ {item.valorTotal.toFixed(2)}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={itens}
              pagination={false}
              rowKey="ITE_ID"
              className="inserir-cotacao-tabela"
            />
          )}

          {modoEdicao && (
            <div className="inserir-cotacao-valor-total">
              <h3>Valor Total da Cotação: R$ {valorTotal.toFixed(2)}</h3>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24 }}>
            <Button
              className="inserir-cotacao-button"
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={handleVoltar}
              style={{ minWidth: 120, fontWeight: 500, background: '#001f4d', border: 'none' }}
            >
              Voltar
            </Button>
            {!modoEdicao ? (
              <Button
                className="inserir-cotacao-button"
                type="primary"
                onClick={handleInserirCotacao}
                style={{ minWidth: 140 }}
              >
                Inserir Cotação
              </Button>
            ) : (
              <Button
                type="primary"
                onClick={handleEnviarCotacao}
                className="inserir-cotacao-button"
                style={{ minWidth: 140 }}
              >
                Enviar Cotação
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InserirCotacao; 